/**
 * The tenant this build talks to.
 *
 * ── Why it is configuration and not a text box ──────────────────────────────
 *
 * Nothing a person can type belongs there. The obvious alternative, resolving
 * the tenant from the email, needs a **cross-tenant read**: `users` is unique on
 * `(tenant_id, email_normalized)` and the runtime role is `NOBYPASSRLS`
 * precisely so it cannot look across tenants. `routers/auth.py` makes the same
 * argument about resolving a tenant *slug*, and adding a `SECURITY DEFINER`
 * escape hatch to spare a demo one form field would be a poor trade. In
 * production the tenant comes from the federated issuer, which is the real
 * answer to the same question.
 *
 * Empty string when unset, which is the signal for the sign-in screen to render
 * the field instead of hiding it — never a hardcoded fallback. A UUID compiled
 * into the bundle is the defect the nav had when "Claims" pointed at one
 * tenant's claim number: correct in the tenant it was copied from and wrong
 * everywhere else.
 *
 * Read per call rather than captured at module load, so a test can stub the
 * variable and exercise both branches. A module-scope `const` made the
 * configured branch unreachable from a test without `vi.resetModules`, and a
 * branch that is awkward to test is a branch that goes untested.
 *
 * Shared by sign-in and by session restore, because a restore has to name the
 * same tenant the cookie's session belongs to. It lived in `SignInPage` until
 * restore needed it too; one copy, because two copies of a rule about which
 * tenant we are is exactly the kind of thing that drifts.
 */
export function configuredTenantId(): string {
  return import.meta.env["VITE_TENANT_ID"] ?? "";
}

const TENANT_STORAGE_KEY = "sarva.tenant-id";

/**
 * The tenant a restore should name, which is not always the configured one.
 *
 * `VITE_TENANT_ID` answers the demo, where one tenant is set once. It does not
 * answer a build where the sign-in screen asks for the tenant, and that is not a
 * hypothetical: the field exists precisely so a developer can work against
 * several tenants. Without this, those builds keep the whole defect GAP-075
 * names — the session cookie survives the reload and the client has forgotten
 * which tenant to present it for.
 *
 * ── Why `localStorage`, when the token is forbidden from it ─────────────────
 *
 * The rule in doc 23 §11a is about the **access token**: a durable credential an
 * injected script can read and replay. A tenant id is neither. It is typed into
 * a form, sent in the clear on every sign-in, and compiled into the bundle
 * whenever `VITE_TENANT_ID` is set — a script that can read it could have read
 * it off the form. It grants nothing on its own; the cookie is what
 * authenticates, and that stays `HttpOnly`.
 *
 * `localStorage` rather than the `sessionStorage` the practice selection uses,
 * because the two are answering different questions. A practice selection is
 * per-tab by design. A pasted deep link opens a **new tab**, which starts with
 * an empty `sessionStorage` and the session cookie fully intact — so
 * `sessionStorage` here would restore a reload and still bounce every deep
 * link, which is the more annoying half of the defect.
 */
export function restorableTenantId(): string {
  // Backend D202: the tenant the last session was made in comes first — on a deployment that
  // serves several organisations it is the only one that matches the cookie — then the
  // organisation this browser chose, then the build's configured one.
  try {
    const remembered = globalThis.localStorage?.getItem?.(TENANT_STORAGE_KEY) ?? "";
    if (remembered !== "") return remembered;
  } catch {
    // A hardened profile or Safari private mode. Restoring a session is never
    // worth taking the application down for; the sign-in screen still works.
  }
  return signInTenantId();
}

const ORGANISATION_KEY = "sarva.organisation";

/** The organisation this browser signs in to, chosen by code or by a set-password link. */
export interface ChosenOrganisation {
  tenantId: string;
  name: string;
}

/**
 * Backend D202 — one deployment, many organisations. Kept apart from the session's tenant
 * above, which is forgotten whenever a restore fails: the choice of organisation is the
 * person's, and a cold start must not quietly switch them back to the build's default.
 */
export function chosenOrganisation(): ChosenOrganisation | null {
  try {
    const raw = globalThis.localStorage?.getItem?.(ORGANISATION_KEY);
    if (raw === null || raw === undefined) return null;
    const parsed = JSON.parse(raw) as Partial<ChosenOrganisation>;
    return typeof parsed.tenantId === "string" && typeof parsed.name === "string"
      ? { tenantId: parsed.tenantId, name: parsed.name }
      : null;
  } catch {
    return null;
  }
}

export function chooseOrganisation(organisation: ChosenOrganisation): void {
  try {
    globalThis.localStorage?.setItem?.(ORGANISATION_KEY, JSON.stringify(organisation));
  } catch {
    // See above.
  }
}

export function forgetOrganisation(): void {
  try {
    globalThis.localStorage?.removeItem?.(ORGANISATION_KEY);
  } catch {
    // See above.
  }
}

/** Where sign-in and forgot-password send a person: their chosen organisation, else the
 * build's. Empty when neither is known — the screen then asks for a code. */
export function signInTenantId(): string {
  return chosenOrganisation()?.tenantId ?? configuredTenantId();
}

/** Remember the tenant a session was established in, for the next page load. */
export function rememberTenantId(tenantId: string): void {
  try {
    globalThis.localStorage?.setItem?.(TENANT_STORAGE_KEY, tenantId);
  } catch {
    // See above.
  }
}

/**
 * Forget it on sign-out.
 *
 * Not for secrecy — it is not a secret — but so that a shared workstation does
 * not present the previous user's tenant to the next one's reload. The cookie is
 * already revoked, so the refresh would fail either way; this keeps the failure
 * from being about somebody else's tenant.
 */
export function forgetTenantId(): void {
  try {
    globalThis.localStorage?.removeItem?.(TENANT_STORAGE_KEY);
  } catch {
    // See above.
  }
}
